/**
 * Lời mời cài app của trình duyệt (`beforeinstallprompt`).
 *
 * File này KHÔNG được import React hay thư viện nào dùng React: `main.tsx` nạp nó TRƯỚC ranh giới
 * bất đồng bộ của Module Federation (chạm `react` ở đó là `loadShareSync` lỗi, app trắng màn —
 * xem ghi chú đầu `main.tsx`). Phải bắt sớm cỡ đó vì trình duyệt chỉ bắn sự kiện MỘT lần mỗi lượt
 * mở trang; trên mạng chậm `bootstrap` tới sau sự kiện thì cả lượt đó không có mục «Cài ứng dụng».
 */

/** Sự kiện riêng của Chromium — `lib.dom.d.ts` không khai. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<unknown>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** `unavailable`: trình duyệt không (còn) giữ lời mời nào — đã cài rồi, hoặc Safari / Firefox. */
export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

let pending: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function publish(event: InstallPromptEvent | null): void {
  pending = event;
  for (const listener of listeners) listener();
}

/**
 * KHÔNG gọi `preventDefault()`: trên Android thanh mời cài của Chrome vẫn tự hiện. Mục trong menu
 * tài khoản là lối thứ hai, không thay lối mặc định — nhân viên trạm không tự tìm ra một mục menu.
 */
export function captureInstallPrompt(): void {
  globalThis.addEventListener('beforeinstallprompt', (event) => publish(event as InstallPromptEvent));
  globalThis.addEventListener('appinstalled', () => publish(null));
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function installPromptAvailable(): boolean {
  return pending !== null;
}

export async function promptInstall(): Promise<InstallOutcome> {
  const event = pending;
  if (!event) return 'unavailable';
  // Mỗi sự kiện chỉ `prompt()` được MỘT lần — dùng xong là bỏ, kể cả khi người dùng từ chối.
  publish(null);
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch {
    return 'unavailable';
  }
}
